import {
  Box,
  Text,
  Table,
  TableContainer,
  Thead,
  Tbody,
  Th,
  Tr,
  Td,
} from '@chakra-ui/react';
import * as _ from 'lodash';
import React from 'react';

import {
  date as commonDate,
  utils as commonUtils,
} from '@finley-technologies/common';

import {
  ConcentrationLimitFormatMap,
  ConcentrationLimitMappedData,
  ExpandedConcentrationAggregateInsightsData,
  PortfolioChartConfig,
  portfolioFormatterMap,
} from '../shared/portfolioChartUtils';
import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';
import { ToggleIcon } from 'packages/common-frontend/components/ToggleIcon';

const headers = [
  'Show',
  '',
  'Property',
  'Limit',
  'Concentration',
  'Amount',
  'Excess Amount',
  'Excess Percent',
];

const ConcentrationCell = ({ label }: { label: string }) => {
  return (
    <Td fontSize="16px" color={colors.black[70]}>
      {label}
    </Td>
  );
};

type PortfolioExcessConcentrationChartDetailsTableProps = {
  selectedBarData: ExpandedConcentrationAggregateInsightsData;
  chartConfig: PortfolioChartConfig<any>;
  chartTitle: string;
  activeFields: (keyof ExpandedConcentrationAggregateInsightsData)[];
  onToggleActiveField: (
    selectedField: keyof ExpandedConcentrationAggregateInsightsData,
  ) => void;
  concentrationLimitMappedData: ConcentrationLimitMappedData;
  concentrationLimitFormatMap: ConcentrationLimitFormatMap;
};
export const PortfolioExcessConcentrationChartDetailsTable = ({
  selectedBarData,
  chartConfig,
  chartTitle,
  activeFields,
  onToggleActiveField,
  concentrationLimitMappedData,
  concentrationLimitFormatMap,
}: PortfolioExcessConcentrationChartDetailsTableProps) => {
  return (
    <Box width="100%">
      <Text fontSize="16px" fontWeight={600} mb="24px">
        Reporting Date:{' '}
        {commonDate.fullDateStrToHumanDateString(selectedBarData.reportingDate)}
      </Text>

      <TableContainer width="100%">
        <Table borderWidth="1px">
          <Thead borderBottomWidth="2px">
            <Tr>
              {_.map(headers, (header) => (
                <Th
                  key={`portfolio-${chartTitle}-table-header-${header}`}
                  textTransform="none"
                  fontSize="16px"
                  fontWeight={500}
                  color={colors.black[95]}
                  letterSpacing="normal"
                  textAlign={header === 'Show' ? 'center' : undefined}
                >
                  {header}
                </Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {_.map(chartConfig, (config, fieldName) => {
              if (
                !config ||
                !concentrationLimitMappedData[selectedBarData.reportingDate] ||
                !concentrationLimitMappedData[selectedBarData.reportingDate][
                  fieldName
                ]
              ) {
                return (
                  <React.Fragment
                    key={`portfolio-${chartTitle}-table-row-${fieldName}`}
                  />
                );
              }
              const { label, format, color } = config;
              const formattedExcessConcentrationAmount = portfolioFormatterMap[
                format
              ](selectedBarData[fieldName]);

              const formattedPercentOfTotalExcessConcentration = portfolioFormatterMap.percentage(
                commonUtils.divideByOrZero(
                  _.toNumber(selectedBarData[fieldName]),
                  selectedBarData.totalConcentrationLimitKickout,
                ),
              );

              const {
                concentration,
                concentrationLimit,
                concentrationAmount,
              } = concentrationLimitMappedData[selectedBarData.reportingDate][
                fieldName
              ];

              const formattedConcentrationAmount = portfolioFormatterMap.currency(
                concentrationAmount,
              );

              const concentrationConfigFormat = concentrationLimitFormatMap.get(
                fieldName,
              );
              const portfolioFormatterMapType =
                concentrationConfigFormat === 'number' ||
                !concentrationConfigFormat
                  ? 'float'
                  : concentrationConfigFormat;
              const formattedConcentration = portfolioFormatterMap[
                portfolioFormatterMapType
              ](concentration);
              const formattedConcentrationLimit = portfolioFormatterMap[
                portfolioFormatterMapType
              ](concentrationLimit);

              const isActive = _.includes(activeFields, fieldName);
              return (
                <Tr key={`portfolio-${chartTitle}-table-row-${fieldName}`}>
                  <Td
                    cursor="pointer"
                    onClick={() => onToggleActiveField(fieldName)}
                    textAlign="center"
                  >
                    <ToggleIcon isActive={isActive} />
                  </Td>
                  <Td textAlign="center">
                    <Icon icon="square" type="solid" color={color} />
                  </Td>
                  <Td
                    fontSize="16px"
                    color={colors.black[70]}
                    wordBreak="break-word"
                    whiteSpace="normal"
                  >
                    {label}
                  </Td>
                  <ConcentrationCell label={formattedConcentrationLimit} />
                  <ConcentrationCell label={formattedConcentration} />
                  <ConcentrationCell label={formattedConcentrationAmount} />
                  <ConcentrationCell
                    label={formattedExcessConcentrationAmount}
                  />
                  <ConcentrationCell
                    label={formattedPercentOfTotalExcessConcentration}
                  />
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
};
